import { useState, useEffect, useCallback, useContext } from 'react';
import { supabase } from '@/lib/supabase';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Clock } from 'lucide-react';

// Session timeout settings (in milliseconds)
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity
const WARNING_BEFORE_TIMEOUT = 5 * 60 * 1000; // Show warning 5 minutes before timeout
const CHECK_INTERVAL = 60 * 1000; // Check every minute

// Import the context directly to use optional check
import { AuthContext } from '@/features/auth/components/AuthProvider';

export function SessionTimeoutWarning() {
  const authContext = useContext(AuthContext);
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(WARNING_BEFORE_TIMEOUT);
  const [isExtending, setIsExtending] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Early return if no auth context (component rendered outside AuthProvider)
  const session = authContext?.session ?? null;
  const signOut = authContext?.signOut;

  // Update last activity on user interaction
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
    if (showWarning) {
      setShowWarning(false);
    }
  }, [showWarning]);

  // Set up activity listeners
  useEffect(() => {
    if (!session) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [session, updateActivity]);

  // Check for session timeout
  useEffect(() => {
    if (!session) return;

    const checkTimeout = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      const timeUntilTimeout = SESSION_TIMEOUT - timeSinceActivity;

      if (timeUntilTimeout <= 0) {
        // Session expired, sign out
        signOut?.();
        setShowWarning(false);
      } else if (timeUntilTimeout <= WARNING_BEFORE_TIMEOUT) {
        // Show warning
        setShowWarning(true);
        setTimeRemaining(timeUntilTimeout);
      } else {
        setShowWarning(false);
      }
    };

    const interval = setInterval(checkTimeout, CHECK_INTERVAL);
    checkTimeout(); // Check immediately

    return () => clearInterval(interval);
  }, [session, lastActivity, signOut]);

  // Update countdown every second when warning is shown
  useEffect(() => {
    if (!showWarning) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      const timeUntilTimeout = SESSION_TIMEOUT - timeSinceActivity;

      if (timeUntilTimeout <= 0) {
        signOut?.();
        setShowWarning(false);
      } else {
        setTimeRemaining(timeUntilTimeout);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [showWarning, lastActivity, signOut]);

  const handleExtendSession = async () => {
    setIsExtending(true);
    try {
      // Refresh the session token
      const { error } = await supabase.auth.refreshSession();
      if (!error) {
        setLastActivity(Date.now());
        setShowWarning(false);
      }
    } catch (error) {
      console.error('Failed to extend session:', error);
    } finally {
      setIsExtending(false);
    }
  };

  const handleSignOut = async () => {
    await signOut?.();
    setShowWarning(false);
  };

  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!session) return null;

  return (
    <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-warning" />
            Session Expiring Soon
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>
              Your session will expire due to inactivity. You will be automatically signed out in:
            </p>
            <p className="text-2xl font-bold text-foreground text-center py-2">
              {formatTime(timeRemaining)}
            </p>
            <p>
              Click "Stay Signed In" to continue your session.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleSignOut}
            disabled={isExtending}
          >
            Sign Out
          </Button>
          <AlertDialogAction
            onClick={handleExtendSession}
            disabled={isExtending}
          >
            {isExtending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Extending...
              </>
            ) : (
              'Stay Signed In'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
